import { useEffect, useRef, useState } from "react";

const DRAG_ACCENT_COLOR = "#7e00da";

function CardDesignText({ style, title, id, onDrag, onActivate }) {
  const elRef = useRef(null);
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      const drag = dragRef.current;
      if (!drag) return;

      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      const maxLeft = Math.max(0, drag.parentWidth - drag.elWidth);
      const maxTop = Math.max(0, drag.parentHeight - drag.elHeight);

      const newLeft = Math.max(0, Math.min(drag.startLeft + dx, maxLeft));
      const newTop = Math.max(0, Math.min(drag.startTop + dy, maxTop));

      const xPct =
        drag.parentWidth > 0
          ? Math.round((newLeft / drag.parentWidth) * 100)
          : 0;
      const yPct =
        drag.parentHeight > 0
          ? Math.round((newTop / drag.parentHeight) * 100)
          : 0;

      onDrag?.(xPct, yPct);
    };

    const handleUp = () => setIsDragging(false);

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);

    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [isDragging, onDrag]);

  const handlePointerDown = (e) => {
    onActivate?.();
    if (!onDrag) return;
    if (e.button !== undefined && e.button !== 0) return;

    const el = elRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;

    const parentRect = parent.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: elRect.left - parentRect.left,
      startTop: elRect.top - parentRect.top,
      parentWidth: parentRect.width,
      parentHeight: parentRect.height,
      elWidth: elRect.width,
      elHeight: elRect.height,
    };

    setIsDragging(true);
    e.preventDefault();
  };

  const draggable = !!onDrag;

  const buildDraggableStyle = () => {
    const base = {
      ...style,
      cursor: isDragging ? "grabbing" : "grab",
      userSelect: "none",
      touchAction: "none",
      transition: isDragging
        ? "box-shadow 120ms ease, transform 120ms ease"
        : "outline-color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
      willChange: "left, top, transform",
    };

    if (isDragging) {
      return {
        ...base,
        outline: `2px solid ${DRAG_ACCENT_COLOR}`,
        outlineOffset: 4,
        boxShadow: `0 12px 28px -8px rgba(126, 0, 218, 0.45), 0 0 0 6px rgba(126, 0, 218, 0.12)`,
        transform: `${style?.transform ? style.transform + " " : ""}scale(1.03)`,
        zIndex: 50,
      };
    }

    if (isHovered) {
      return {
        ...base,
        outline: `2px dashed ${DRAG_ACCENT_COLOR}`,
        outlineOffset: 3,
        boxShadow: `0 0 0 4px rgba(126, 0, 218, 0.08)`,
      };
    }

    return base;
  };

  const finalStyle = draggable ? buildDraggableStyle() : style;

  return (
    <p
      ref={elRef}
      className="giftCardText absolute"
      style={finalStyle}
      id={id}
      title={draggable ? "اسحب لتغيير الموضع" : undefined}
      onPointerDown={handlePointerDown}
      onPointerEnter={draggable ? () => setIsHovered(true) : undefined}
      onPointerLeave={draggable ? () => setIsHovered(false) : undefined}
    >
      {title}
    </p>
  );
}

export default CardDesignText;
